import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useToasts } from './TactileToasts';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, userId }) => {
  const { showToast } = useToasts();
  const [category, setCategory] = useState<'General' | 'Bug Report' | 'Feature Request' | 'Outfit Quality'>('General');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      showToast("Please provide both subject and message details.", "error");
      return;
    }

    setIsSubmitting(true);
    showToast("Recording feedback into Telemetry Vault...", "logic");

    try {
      const response = await fetch('/api/sendFeedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, subject, message })
      });

      const data = await response.json();

      if (response.ok) {
        showToast(`✨ Feedback recorded successfully in Telemetry Vault!`, "success");
        setSubject('');
        setMessage('');
        onClose();
      } else {
        showToast(data.error || "Feedback recording failed.", "error");
      }
    } catch (err: any) {
      showToast("Network error recording feedback.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedSubjectHeader = `Feedback-synclothe-${userId || 'user'}`;

  return createPortal(
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[99999] p-4 animate-fade-in">
      <div className="bg-[#0b0f19] border-2 border-azure-500/60 p-6 md:p-8 rounded-3xl max-w-lg w-full shadow-[0_0_80px_rgba(0,0,0,0.95)] opacity-100 text-white relative overflow-hidden">
        <div className="flex justify-between items-start border-b border-white/10 pb-4 mb-6">
          <div>
            <span className="text-[9px] font-black uppercase text-azure-400 tracking-[0.3em] block mb-1">Direct Telemetry Feedback Protocol</span>
            <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter text-white">Submit <span className="text-azure-500">Feedback</span></h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-sm font-bold p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-[#131b2e] border border-azure-500/30 rounded-xl">
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Telemetry Tracking ID</p>
            <p className="text-xs font-mono text-gold-400 font-bold mt-1">{formattedSubjectHeader}</p>
            <p className="text-[9px] text-gray-400 mt-0.5">Persisted directly to Admin Telemetry Dashboard</p>
          </div>

          <div>
            <label className="block text-[10px] uppercase font-black text-gray-400 mb-1 tracking-wider">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as any)}
              className="w-full input-glass text-xs text-white bg-[#0e1424] border border-white/10 rounded-xl p-3 focus:border-azure focus:outline-none"
            >
              <option value="General">General Feedback</option>
              <option value="Bug Report">Bug Report</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Outfit Quality">Outfit Quality & AI Styling</option>
            </select>
          </div>

          <div>
            <label className="block text-[10px] uppercase font-black text-gray-400 mb-1 tracking-wider">Subject Summary</label>
            <input
              type="text"
              id="feedback-subject"
              name="subject"
              autoComplete="off"
              autoCapitalize="sentences"
              autoCorrect="on"
              spellCheck={true}
              placeholder="e.g. Wardrobe rotation suggestion or UI issue..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full input-glass text-xs text-white bg-[#0e1424] border border-white/10 rounded-xl p-3 focus:border-azure focus:outline-none"
              maxLength={150}
              required
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase font-black text-gray-400 mb-1 tracking-wider">Detailed Message</label>
            <textarea
              id="feedback-message"
              name="message"
              autoComplete="off"
              autoCapitalize="sentences"
              autoCorrect="on"
              spellCheck={true}
              placeholder="Describe your feedback, issue, or feature idea in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full input-glass text-xs text-white bg-[#0e1424] border border-white/10 rounded-xl p-3 focus:border-azure focus:outline-none h-28 resize-none"
              maxLength={2000}
              required
            />
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/3 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 text-xs font-black uppercase tracking-widest rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-2/3 btn-azure py-3 px-4 text-xs font-black uppercase tracking-widest rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  <span>Recording...</span>
                </>
              ) : (
                <span>Submit Feedback</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackModal;
